import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  activityLogApiClient,
  authApiClient,
  communicationApiClient,
  mxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";

export const getAllMeterCycle = async (
  page: string,
  search: string,
  meterType: string,
  status: string,
  startDate: string,
  endDate: string,
  utilityId: string,
) => {
  const formatMeterType = (value: string) =>
    value.toUpperCase().split(/\s+/).join("_");
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-cycle/v1",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("page", page);
      if (search) {
        query.push("search_data", search);
      }
      if (meterType) {
        query.push("meter_type", formatMeterType(meterType));
      }
      if (status) {
        query.push("status", status);
      }
      if (startDate) {
        query.push("start_date", startDate);
      }
      if (endDate) {
        query.push("end_date", endDate);
      }
      query.push("is_consumer_count", "1");
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const exportAllMeterCycles = async (
  page: any,
  utilityId: string,
  search?: string,
  meterType?: string,
  status?: string,
  startDate?: string,
  endDate?: string,
): Promise<any> => {
  const formatMeterType = (value: string) =>
    value.toUpperCase().split(/\s+/).join("_");

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-cycle/v1",
    (query) => {
      query.push("remote_utility_id", utilityId);
      if (page) query.push("page", page);
      query.push("is_consumer_count", "1");

      if (search) {
        query.push("search_data", search);
      }

      if (meterType) {
        query.push("meter_type", formatMeterType(meterType));
      }

      if (status) {
        query.push("status", status);
      }

      if (startDate) {
        query.push("start_date", startDate);
      }

      if (endDate) {
        query.push("end_date", endDate);
      }

      query.push("export_format", "csv");
    },
  );

  const response = await mxApiClient.get(url);

  return response.data;
};

export const getUtilityServices = async (utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("utility_id", utilityId);
    },
  );

  const response = await authApiClient.get(url);
  return response.data.result ?? [];
};

export const getReadCycleSummary = async (utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "new-meter-cycle/summary",
    (query) => {
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getReadCycleDetails = async (cycleId: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle-detail/v1/${cycleId}`,
    (query) => {
      query.push("is_extra_data", "1");
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getReadCycleDetailsforEdit = async (cycleId: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle-detail/v1/${cycleId}`,
    (query) => {
      query.push("is_extra_data", "1");
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getRouteDetails = async (
  utilityId: string,
  page?: any,
  limit?: any,
  cycle_id?: any,
  utilityService?: any,
  readType?: any,
  searchQuery?: any,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `route/v1`,
    (query) => {
      query.push("is_pagination_required", true);

      query.push("remote_utility_id", utilityId);
      if (page) {
        query.push("page", page);
        query.push("limit", limit);
      }
      if (cycle_id) {
        query.push("cycle_id", cycle_id);
      }
      if (searchQuery) {
        query.push("search_data", searchQuery);
      }
      if (utilityService) {
        query.push("utility_service", utilityService);
      }
      if (readType) {
        query.push("read_type", readType);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const getAssignedRoutes = async (
  utilityId: string,
  page?: any,
  limit?: any,
  cycle_id?: any,
  utilityService?: any,
  searchQuery?: any,
  isAssigned?: number,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `route`,
    (query) => {
      query.push("is_pagination_required", true);

      query.push("remote_utility_id", utilityId);
      if (page) {
        query.push("page", page);
        query.push("limit", limit);
      }
      if (cycle_id) {
        query.push("meter_cycle_id", cycle_id);
      }
      if (searchQuery) {
        query.push("search_data", searchQuery);
      }
      if (utilityService) {
        query.push("utility_service", utilityService);
      }
      if (isAssigned) {
        query.push("is_assigned", isAssigned);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const getMetersDetails = async (
  utilityId: string,
  cycle_id: any,
  page: any,
  search_data?: any,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-count`,
    (query) => {
      query.push("is_consumer_count", "1");
      query.push("is_consumer_info", "1");
      query.push("meter_cycle_id", cycle_id);
      query.push("remote_utility_id", utilityId);
      query.push("page", page);
      if (search_data) {
        query.push("search_data", search_data);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const searchMetersDetails = async (
  cycle_id: any,
  page: any,
  search_data: string,
  utilityId: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-count`,
    (query) => {
      query.push("meter_cycle_id", cycle_id);
      query.push("remote_utility_id", utilityId);
      query.push("page", page);
      query.push("search_data", search_data);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.results ?? [];
};

export const readCycleAuditTrail = async (cycle_id: any, utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "activityLog",
    `logs`,
    (query) => {
      query.push("patient_id", cycle_id);
      query.push("remote_utility_id", utilityId);
      query.push("action_type", "meter_cycle");
      query.push("module", "mx");
    },
  );
  const response = await communicationApiClient.get(url);
  return response.data.results ?? [];
};

export const getMeterSummaryDashboard = async (
  cycle_id: any,
  utilityId: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle/dashboard`,
    (query) => {
      query.push("cycle_id", cycle_id);
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const createReadCycle = async (payload: any): Promise<any> => {
  try {
    const response = await mxApiClient.post("meter-cycle/v1/", payload);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const updateReadCycle = async (
  payload: any,
  cycleId: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle/${cycleId}`,
  );
  const response = await mxApiClient.put(url, payload);
  return response.data.result ?? [];
};

export const getArea = async (utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "area");
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getSubArea = async (code_list: any, utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "sub_area");
      for (const code of code_list) {
        query.push("code_list", code);
      }
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterType = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choices/meter_type`,
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterSummaryDashboardByRouteId = async (
  utilityId: string,
  route_id?: any[],
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle/dashboard`,
    (query) => {
      if (route_id) {
        for (const id of route_id) {
          query.push("route_id", id);
        }
      }
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const pauseReadCycle = async (utilityId: string, meterCycleId: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `pause-meter-cycle/${meterCycleId}`,
    (query) => {
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.put(url);
  return response.data.result ?? [];
};
export const getFrequency = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choices/frequency_type`,
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};
export const getWeekDays = async (remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choices/weekday`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};
export const getMonths = async (remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `choices/month`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getPerformanceDashboard = async (
  meterCycleId: any,
  utilityId: string,
  runId?: number,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle/performance/${meterCycleId}`,
    (query) => {
      query.push("remote_utility_id", utilityId);
      if (runId) {
        query.push("run_id", runId);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const createScheduleReadCycle = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters("mx", `meter-schedule`);
  const response = await mxApiClient.post(url, payload);
  return response.data.result ?? [];
};

export const updateScheduleReadCycle = async (
  scheduleId: any,
  payload: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-schedule/${scheduleId}`,
  );
  const response = await mxApiClient.patch(url, payload);
  return response.data.result ?? [];
};

const getValidationRules = async (remoteUtilityId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "utility-rule",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result || [];
};

export const getMeterRunHistory = async (
  cycle_id?: any,
  utilityId?: string,
  page?: any,
  searchQuery?: any,
  status?: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-run`,
    (query) => {
      query.push("remote_utility_id", utilityId);
      if (page) {
        query.push("page", page);
        query.push("limit", 10);
      }
      if (cycle_id) {
        query.push("meter_cycle_id", cycle_id);
      }
      if (searchQuery) {
        query.push("search_data", searchQuery);
      }
      if (status) {
        query.push("status", status);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const getAllMeterCycleForMoveRoutes = async (
  page: number,
  search: string,
  meterType: string,
  status: string,
  utilityId: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-cycle",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("page", page);
      query.push("limit", 10);
      if (search) {
        query.push("search_data", search);
      }
      if (meterType) {
        query.push("meter_type", meterType);
      }
      if (status) {
        query.push("status", status);
      }
      query.push("is_consumer_count", "1");
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const updateRoute = async (payload: any): Promise<any> => {
  try {
    const response = await mxApiClient.post("meter-cycle/move-route/", payload);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getRules = async (
  ruleType: number,
  utilityId: string,
  meterCycleId?: number,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `rules`,
    (query) => {
      query.push("rule_type", ruleType);
      query.push("remote_utility_id", utilityId);
      if (meterCycleId) {
        query.push("meter_cycle_id", meterCycleId);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const createRules = async (payload: any): Promise<any> => {
  try {
    const response = await mxApiClient.post("rules/", payload);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const addRoutes = async (payload: any): Promise<any> => {
  try {
    const response = await mxApiClient.post("meter-cycle/add-route/", payload);
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getPremise = async (
  remote_utility_id: string,
  code_name: any[],
  page?: number,
  limit?: number,
  area?: string,
  sub_area?: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      for (const code of code_name) {
        query.push("code_name", code);
      }
      if (page) {
        query.push("page", page);
        query.push("limit", limit);
      }
      if (area) {
        query.push("area", area);
      }
      if (sub_area) {
        query.push("sub_area", sub_area);
      }
      query.push("is_pagination_required", true);
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data ?? [];
};
export const getPremiseBySearch = async (
  remote_utility_id: string,
  code_name: any[],
  search_field: string,
  searchQuery?: string,
  page?: number,
  limit?: number,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "search/onboarding_config",
    (query) => {
      query.push("remote_utility_id", remote_utility_id);
      for (const code of code_name) {
        query.push("code_name", code);
      }

      query.push("search_data", searchQuery);
      if (page) {
        query.push("page", page);
        query.push("limit", limit);
      }
      query.push("search_field", search_field);
      query.push("is_pagination_required", true);
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data ?? [];
};

export const getTerritoryConfigMap = async (
  configLevel: "area" | "sub_area" | "premise" = "area",
  remoteUtilityId: string,
  codeList?: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", remoteUtilityId);
      qs.push("config_level", configLevel);
      if (codeList) {
        qs.push("code_list", codeList);
      }
    },
  );
  const response = await onboardingApiClient.get(url);

  const data = response.data?.result;

  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getScheduleReview = async (
  frequency?: string,
  startDate?: string,
  time?: string,
  endDate?: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `schedule-preview`,
    (query) => {
      if (frequency) {
        query.push("frequency", frequency);
      }
      if (startDate) {
        query.push("start_date", startDate);
      }
      if (time) {
        query.push("time", time);
      }
      if (endDate) {
        query.push("end_date", endDate);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterScheduleList = async (
  remoteUtilityId: string,
  page?: number,
  limit?: number,
  frequency?: string,
  status?: string,
  readType?: string,
  startDate?: string,
  endDate?: string,
  search?: string,
) => {
  const formatMeterType = (value: string) =>
    value.toUpperCase().split(/\s+/).join("_");
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-schedule`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_consumer_count", 1);
      if (page) {
        query.push("page", page);
        query.push("limit", limit);
      }
      if (frequency) {
        query.push("frequency", frequency);
      }
      if (status) {
        query.push("status", status);
      }
      if (readType) {
        query.push("meter_type", formatMeterType(readType));
      }
      if (startDate) {
        query.push("start_date", startDate);
      }
      if (endDate) {
        query.push("end_date", endDate);
      }
      if (search) {
        query.push("search_data", search);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const getMeterScheduleSummary = async (remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-schedule/summary`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterScheduleDetails = async (
  remoteUtilityId: string,
  scheduleId: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-schedule`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("schedule_id", scheduleId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.results ?? [];
};

export const pauseMeterSchedule = async (payload: any): Promise<any> => {
  try {
    const response = await mxApiClient.put(
      `pause-meter-schedule/${payload.schedule_id}/`,
      payload,
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const runMeterSchedule = async (payload: any): Promise<any> => {
  try {
    const response = await mxApiClient.put(
      `meter-schedule-run/${payload.schedule_id}/`,
      payload,
    );
    return response.data.result || response.data;
  } catch (error) {
    throw error;
  }
};

export const getMeterRunHistoryForSchedule = async (
  schedule_id: any,
  utilityId: string,
  page?: any,
  searchQuery?: any,
  status?: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-run`,
    (query) => {
      query.push("remote_utility_id", utilityId);
      if (page) {
        query.push("page", page);
        query.push("limit", 10);
      }
      query.push("meter_schedule_id", schedule_id);
      if (searchQuery) {
        query.push("search_data", searchQuery);
      }
      if (status) {
        query.push("status", status);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const getAllReadCycle = async (utilityId: string, status: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-cycle",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("status", status);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.results ?? [];
};

export const api = {
  meterCycle: {
    landing: {
      list: getAllMeterCycle,
      summary: getReadCycleSummary,
      exportAllMeterCycles: exportAllMeterCycles,
    },
    view: {
      details: getReadCycleDetails,
      routeDetails: getRouteDetails,
      metersDetails: getMetersDetails,
      searchMetersDetails: searchMetersDetails,
      auditTrail: readCycleAuditTrail,
      getValidationRules: getValidationRules,
      getMeterRunHistory: getMeterRunHistory,
      getPerformanceDashboard: getPerformanceDashboard,
      getAllMeterCycleForMoveRoutes: getAllMeterCycleForMoveRoutes,
      updateRoute: updateRoute,
      getRules: getRules,
      createRules: createRules,
      addRoutes: addRoutes,
      getPremise: getPremise,
      getTerritoryConfigMap: getTerritoryConfigMap,
      getAssignedRoutes: getAssignedRoutes,
      getPremiseBySearch: getPremiseBySearch,
    },
    update: {
      pauseReadCycle: pauseReadCycle,
      editReadCycleDetails: getReadCycleDetailsforEdit,
    },
    dashboard: {
      getMeterSummaryDashboard: getMeterSummaryDashboard,
      getMeterSummaryDashboardByRouteId: getMeterSummaryDashboardByRouteId,
    },
    create: {
      createReadCycle: createReadCycle,
      updateReadCycle: updateReadCycle,
      getFrequency: getFrequency,
      getWeekDays: getWeekDays,
      getMonths: getMonths,
      createScheduleReadCycle: createScheduleReadCycle,
      getScheduleReview: getScheduleReview,
    },
    onboarding: {
      getArea: getArea,
      getSubArea: getSubArea,
    },
    utility: {
      getServices: getUtilityServices,
    },
    meterType: {
      getMeterType: getMeterType,
    },
  },
  schedule: {
    create: {
      updateScheduleReadCycle: updateScheduleReadCycle,
      getAllReadCycle: getAllReadCycle,
    },
    list: {
      getMeterScheduleList: getMeterScheduleList,
    },
    dashboard: {
      getMeterScheduleSummary: getMeterScheduleSummary,
    },
    details: {
      getMeterScheduleDetails: getMeterScheduleDetails,
      pauseMeterSchedule: pauseMeterSchedule,
      runMeterSchedule: runMeterSchedule,
      getMeterRunHistoryForSchedule: getMeterRunHistoryForSchedule,
    },
  },
};
